import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, hydrate } from '@tanstack/react-query';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { supabase } from './lib/supabase';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';

// Handle React refresh preamble issues gracefully
if (typeof window !== 'undefined') {
  // Suppress the preamble error by overriding the error
  const originalError = console.error;
  console.error = (...args) => {
    const errorMessage = args[0]?.toString() || '';
    const allArgs = args.join(' ');
    
    if (errorMessage.includes("can't detect preamble")) {
      return; // Completely suppress this error
    }
    // Suppress PostHog web vitals errors
    if (errorMessage.includes('[PostHog.js] [Web Vitals]')) {
      return;
    }
    // Suppress Grammarly extension errors
    if (errorMessage.includes('chrome-extension://kbfnbcaeplbcioakkpcpgfkobkghlhen') ||
        errorMessage.includes('Grammarly.js') ||
        errorMessage.includes('grm ERROR') ||
        errorMessage.includes('Cannot read properties of undefined (reading \'_tag\')') ||
        errorMessage.includes('data-grammarly')) {
      return;
    }
    // Suppress leadsy.ai CORS errors in development
    if (allArgs.includes('leadsy.ai') || 
        allArgs.includes('wvbknd.leadsy.ai') ||
        allArgs.includes('r2.leadsy.ai') ||
        allArgs.includes('website-visitors') ||
        allArgs.includes('Access-Control-Allow-Origin') ||
        allArgs.includes('CORS policy') ||
        allArgs.includes('tag.js') ||
        (allArgs.includes('Failed to fetch') && (allArgs.includes('leadsy') || allArgs.includes('tag.js'))) ||
        (allArgs.includes('ERR_FAILED') && (allArgs.includes('leadsy') || allArgs.includes('tag.js'))) ||
        (allArgs.includes('429') && allArgs.includes('leadsy')) ||
        (errorMessage.includes('leadsy') && errorMessage.includes('CORS')) ||
        (errorMessage.includes('tag.js') && errorMessage.includes('Failed to fetch'))) {
      return;
    }
    originalError.apply(console, args);
  };
  
  // Also suppress the error at the global level
  window.addEventListener('error', (event) => {
    if (event.message && event.message.includes("can't detect preamble")) {
      event.preventDefault();
      return;
    }
    // Suppress PostHog errors
    if (event.message && event.message.includes('[PostHog.js]')) {
      event.preventDefault();
    }
    // Suppress leadsy.ai CORS errors
    if (event.message && (
      event.message.includes('leadsy.ai') ||
      event.message.includes('wvbknd.leadsy.ai') ||
      event.message.includes('CORS') ||
      (event.message.includes('Failed to fetch') && event.message.includes('leadsy'))
    )) {
      event.preventDefault();
      return;
    }
    // Suppress cookie consent 403 errors
    if (event.message && event.message.includes('cookie_consents') && event.message.includes('403')) {
      event.preventDefault();
      return;
    }
    // Suppress TealHQ auth 401 errors
    if (event.message && event.message.includes('auth.service.tealhq.com') && event.message.includes('401')) {
      event.preventDefault();
      return;
    }
    // Suppress content.js errors (browser extension)
    if (event.message && event.message.includes('content.js')) {
      event.preventDefault();
      return;
    }
    // Suppress Grammarly errors
    if (event.message && (
      event.message.includes('chrome-extension://kbfnbcaeplbcioakkpcpgfkobkghlhen') ||
      event.message.includes('Grammarly.js') ||
      event.message.includes('grm ERROR') ||
      event.message.includes('Cannot read properties of undefined (reading \'_tag\')') ||
      event.message.includes('data-grammarly')
    )) {
      event.preventDefault();
    }
    // Suppress leadsy.ai CORS errors in development
    if (event.message && (
      event.message.includes('leadsy.ai') ||
      event.message.includes('wvbknd.leadsy.ai') ||
      event.message.includes('r2.leadsy.ai') ||
      event.message.includes('Access-Control-Allow-Origin') ||
      event.message.includes('CORS policy') ||
      event.message.includes('website-visitors') ||
      event.message.includes('tag.js') ||
      (event.message.includes('Failed to fetch') && event.message.includes('leadsy')) ||
      (event.filename && (event.filename.includes('tag.js') || event.filename.includes('leadsy')))
    )) {
      event.preventDefault();
      return false;
    }
  });
  
  // Suppress unhandled promise rejections from leadsy.ai, tag.js, and content.js
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const reasonString = reason?.toString() || '';
    const reasonMessage = reason?.message || '';
    const errorString = reason?.error?.toString() || '';
    
    if (reasonString.includes('leadsy.ai') || 
        reasonString.includes('wvbknd.leadsy.ai') ||
        reasonString.includes('r2.leadsy.ai') ||
        reasonString.includes('tag.js') ||
        reasonString.includes('content.js') ||
        reasonString.includes('CORS') ||
        reasonMessage.includes('leadsy.ai') ||
        reasonMessage.includes('tag.js') ||
        reasonMessage.includes('content.js') ||
        reasonMessage.includes('Failed to fetch') ||
        reasonMessage.includes('CORS') ||
        errorString.includes('leadsy') ||
        errorString.includes('tag.js') ||
        errorString.includes('content.js') ||
        errorString.includes('CORS')) {
      event.preventDefault();
      return false;
    }
  });
  
  // Suppress fetch errors from leadsy.ai and expected API errors
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    
    // Block leadsy.ai requests in development to avoid CORS errors
    if ((url.includes('leadsy.ai') || url.includes('wvbknd.leadsy.ai') || url.includes('r2.leadsy.ai')) && import.meta.env.DEV) {
      // In development, silently fail leadsy.ai requests
      // Return a rejected promise that won't log errors
      return Promise.reject(new Error('Leadsy.ai request blocked in development')).catch(() => {
        // Silently catch to prevent unhandled promise rejection warnings
      });
    }
    
    const promise = originalFetch.apply(this, args);
    
    // Suppress console errors for leadsy.ai fetch failures
    if (import.meta.env.DEV && (url.includes('leadsy.ai') || url.includes('wvbknd.leadsy.ai') || url.includes('r2.leadsy.ai'))) {
      promise.catch(() => {
        // Silently catch leadsy.ai fetch errors in development
      });
    }
    
    // Suppress expected 403/401 errors for cookie_consents and TealHQ auth
    promise.catch((error) => {
      if (url.includes('cookie_consents') && (error.status === 403 || error.message?.includes('403'))) {
        // Expected RLS permission error - don't log
        return;
      }
      if (url.includes('auth.service.tealhq.com') && (error.status === 401 || error.message?.includes('401'))) {
        // Expected auth error when not authenticated - don't log
        return;
      }
      // Re-throw other errors so they can be handled normally
      throw error;
    });
    
    return promise;
  };
}

// Initialize Sentry (client-side only)

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.MODE || (import.meta.env.NODE_ENV || 'development');

if (sentryDsn) {

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Override localhost filtering for development
    beforeSend(event) {
      if (sentryEnvironment === 'development') {
        // Allow all events in development mode (including localhost)
        return event;
      }
      return event;
    },
    
    // Additional config to ensure localhost events are not ignored
    ignoreErrors: [],
    denyUrls: [],
    
    // Debug mode for development
    debug: sentryEnvironment === 'development',
  });
  
}

// Get the dehydrated state from the window object
const dehydratedState = (window as any).__DEHYDRATED_STATE__;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Hydrate the query client with the dehydrated state
if (dehydratedState) {
  console.log('🔍 [HYDRATION] Hydrating with state:', dehydratedState);
  hydrate(queryClient, dehydratedState);
} else {
  console.log('🔍 [HYDRATION] No dehydrated state found');
}

// Ensure we're hydrating the same content that was rendered on the server
const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <SessionContextProvider supabaseClient={supabase}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </SessionContextProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// Use hydrateRoot when SSR content exists, createRoot otherwise
// This properly hydrates SSR content instead of replacing it
// Check for SSR content more reliably
const hasSSRContent = root.children.length > 0 || 
                      (root.innerHTML && root.innerHTML.trim() !== '' && 
                       !root.innerHTML.includes('<!--app-html-->'));

if (hasSSRContent) {
  console.log('🔍 [HYDRATION] SSR content detected, using hydrateRoot');
  console.log('🔍 [HYDRATION] Root children:', root.children.length);
  console.log('🔍 [HYDRATION] Root innerHTML length:', root.innerHTML.length);
  try {
    ReactDOM.hydrateRoot(root, app);
  } catch (error) {
    console.error('🔍 [HYDRATION] Hydration error, falling back to createRoot:', error);
    // Fallback to createRoot if hydration fails
    root.innerHTML = '';
    ReactDOM.createRoot(root).render(app);
  }
} else {
  console.log('🔍 [HYDRATION] No SSR content detected, using createRoot');
  console.log('🔍 [HYDRATION] Root children:', root.children.length);
  console.log('🔍 [HYDRATION] Root innerHTML:', root.innerHTML.substring(0, 100));
  ReactDOM.createRoot(root).render(app);
} 